import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useLocation } from "react-router-dom";
import { FlatTab, type FlatTabItem } from "@/components/FlatTab";
import { useBreadcrumb } from "@/components/ui/Breadcrumb";
import { Skeleton } from "@/components/ui";
import { FatalError } from "@/components/FatalError";
import { useLiveEventDetails } from "@/hooks/useLiveEventDetails";
import { ROUTES } from "@/routes/paths";
import { Details } from "./Details";
import { Results } from "./Results";

export const EventDetails = () => {
  const { t } = useTranslation();
  const { setDynamicTitle } = useBreadcrumb();
  const { eventId } = useParams<{ eventId: string }>();
  const location = useLocation();

  const getCurrentTab = (): "details" | "results" => {
    return location.pathname.includes(
      ROUTES.COMMUNITY_TOOLS.EVENT_DETAILS.RESULTS,
    )
      ? "results"
      : "details";
  };

  const activeTab = getCurrentTab();

  const { liveEvent, loading, error } = useLiveEventDetails(eventId);

  useEffect(() => {
    if (liveEvent?.name) {
      setDynamicTitle(liveEvent.name);
    } else if (!loading && !liveEvent) {
      setDynamicTitle(t("eventDetails.fallbackTitle"));
    }
  }, [liveEvent, loading, setDynamicTitle, t]);

  useEffect(() => {
    return () => {
      setDynamicTitle(null);
    };
  }, [setDynamicTitle]);

  const tabItems: FlatTabItem[] = [
    {
      label: t("eventDetails.tabs.details"),
      active: activeTab === "details",
      route: "eventDetails",
      params: { eventId: eventId || "" },
    },
    {
      label: t("eventDetails.tabs.results"),
      active: activeTab === "results",
      route: "eventResults",
      params: { eventId: eventId || "" },
    },
  ];

  if (error) {
    return <FatalError />;
  }

  const eventTitle = liveEvent?.name || t("eventDetails.fallbackTitle");

  return (
    <div className="min-h-screen bg-background">
      <div className="w-full pt-6 pr-6 pl-8">
        {loading ? (
          <Skeleton className="mb-4 h-8 w-64" />
        ) : (
          <h1 className="mb-4 text-2xl font-semibold text-foreground">
            {eventTitle}
          </h1>
        )}
      </div>

      <div className="ml-8">
        <FlatTab items={tabItems} />
      </div>

      <div className="w-full border-b border-border"></div>

      <div className="px-8 pt-8">
        {activeTab === "details" && (
          <Details liveEvent={liveEvent} loading={loading} error={error} />
        )}
        {activeTab === "results" && eventId && <Results eventId={eventId} />}
      </div>
    </div>
  );
};
